import debounce from 'lodash.debounce';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import useIsMounted from '@alltrails/core/hooks/useIsMounted';
import { OnSelect, ResultBase, Results } from '../../types/searchBoxTypes';
import FullscreenSearchBox from '../FullscreenSearchBox';

export type CustomSearchBoxProps<T extends ResultBase> = {
  className?: string;
  clearOnSelect?: boolean;
  debounceMs?: number;
  fetchResultsRequest: (query: string) => void;
  onHideResults?: () => void;
  onShowResults?: () => void;
  onSelect: OnSelect<T>;
  results: Results<T>;
  testId?: string;
  validateQuery?: (query: string) => boolean;
  value?: string;
  searchRequestLoading?: boolean;
} & Pick<
  ComponentProps<typeof FullscreenSearchBox<T>>,
  | 'dropdownVariant'
  | 'emptyState'
  | 'inputMaxLength'
  | 'onInputBlur'
  | 'onInputFocus'
  | 'placeholder'
  | 'renderAdditionalContent'
  | 'renderResultContent'
  | 'searchResultsTabBar'
  | 'size'
  | 'tertiaryElement'
  | 'variant'
>;

/**
 * A search box component for UIs that require a result to be selected to commit the search.
 * Ex: Algolia search, where a user must select a result to navigate to the result's page.
 */
const CustomSearchBox = <T extends ResultBase>({
  className,
  clearOnSelect,
  debounceMs = 500,
  dropdownVariant,
  emptyState,
  fetchResultsRequest,
  inputMaxLength,
  onHideResults,
  onInputBlur,
  onInputFocus,
  onShowResults,
  onSelect,
  placeholder,
  renderAdditionalContent,
  renderResultContent,
  results,
  searchResultsTabBar,
  size = 'md',
  tertiaryElement,
  testId,
  validateQuery,
  searchRequestLoading,
  variant,
  value = ''
}: CustomSearchBoxProps<T>) => {
  const [query, setQuery] = useState(value);
  const isMounted = useIsMounted();
  const [areResultsLoading, setAreResultsLoading] = useState(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const search = useCallback(
    (searchQuery: string) => {
      if (isMounted() && (!validateQuery || validateQuery(searchQuery))) {
        fetchResultsRequest(searchQuery);
      }
    },
    [fetchResultsRequest, isMounted, validateQuery]
  );

  // Gets an initial set of results and updates results when fetchResults changes
  useEffect(() => {
    const updateResults = async () => {
      setAreResultsLoading(true);
      await search(query);
      setAreResultsLoading(false);
    };
    updateResults();
    // Don't want to trigger this when query changes
  }, [search]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async text => {
        await search(text);
        setAreResultsLoading(false);
      }, debounceMs),
    [debounceMs, search]
  );

  const onChange = useCallback(
    (text: string) => {
      setQuery(text);
      debouncedSearch(text);
      setAreResultsLoading(true);
    },
    [debouncedSearch]
  );

  const onItemSelect: OnSelect<T> = useCallback(
    (result, additionalInfo) => {
      onSelect(result, additionalInfo);
      if (clearOnSelect) {
        setQuery('');
      }
    },
    [onSelect, clearOnSelect]
  );

  const onCancelMobileSearch = useCallback(() => {
    setQuery('');
  }, []);

  return (
    <FullscreenSearchBox
      className={className}
      dropdownVariant={dropdownVariant}
      emptyState={emptyState}
      isLoading={areResultsLoading}
      inputMaxLength={inputMaxLength}
      onCommit={onItemSelect}
      onHideResults={onHideResults}
      onInputBlur={onInputBlur}
      onInputFocus={onInputFocus}
      onMobileCancel={onCancelMobileSearch}
      onQueryChange={onChange}
      onShowResults={onShowResults}
      placeholder={placeholder}
      query={query}
      renderAdditionalContent={renderAdditionalContent}
      renderResultContent={renderResultContent}
      requireSelection
      results={results}
      searchResultsTabBar={searchResultsTabBar}
      size={size}
      tertiaryElement={tertiaryElement}
      testId={testId}
      validateQuery={validateQuery}
      searchRequestLoading={searchRequestLoading}
      variant={variant}
    />
  );
};

export default CustomSearchBox;
